import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  styled,
  Tooltip,
  ToggleButton,
} from "@mui/material";
import { Cookie, MenuOpen as MenuOpenIcon } from "@mui/icons-material";

import {
  Person as PersonIcon,
  PostAdd as PostAddIcon,
  Group as GroupIcon,
  Settings as SettingsIcon,
  ExitToApp as LogoutIcon,
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Assignment as AssignmentIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import Cookies from "js-cookie";
import { useProfile } from "../../../contexts/ProfileContext";
import { ToastContainer } from "react-toastify";

const drawerWidth = 240;

const Main = styled("main")(({ theme }) => ({
  flexGrow: 1,
  padding: 0,
  marginLeft: drawerWidth,
  marginTop: 64,
  width: `calc(100% - ${drawerWidth}px)`,
  margin: 0,
  alignItems: "flex-start",
}));

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  "& .MuiDrawer-paper": {
    width: drawerWidth,
    boxSizing: "border-box",
    marginTop: 64,
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
}));

const menuItems = [
  { text: "Profile", icon: <PersonIcon />, path: "/corporate/profile" },
  {
    text: "Job Postings",
    icon: <PostAddIcon />,
    path: "/corporate/job-postings",
  },
  {
    text: "Interviews",
    icon: <AssignmentIcon />,
    path: "/corporate/interviews",
  },
  {
    text: "Discover Candidates",
    icon: <GroupIcon />,
    path: "/corporate/discover-candidates",
  },
  //{ text: 'Settings', icon: <SettingsIcon />, path: '/corporate/settings' },
];

const CorporateLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { corporateRequiresProfile, resetProfileContext } = useProfile();
  const [userInitials, setUserInitials] = useState("");
  const [isOpenSidebar, setIsOpenSidebar] = useState(
    Cookies.get("sidebarState")
      ? Cookies.get("sidebarState") !== "closed"
      : false
  );

  // Filter menu items based on requiresProfile status
  const visibleMenuItems =
    corporateRequiresProfile === true
      ? menuItems.filter((item) => item.path === "/corporate/profile")
      : menuItems;

  useEffect(() => {
    const token = Cookies.get("token");
    if (token) {
      const decodedToken = jwtDecode(token);
      const email = decodedToken.email || "";
      const initials = email.split("@")[0].substring(0, 2).toUpperCase();
      setUserInitials(initials);
    }
  }, []);

  const handleLogout = () => {
    // Reset profile context state
    if (resetProfileContext) {
      resetProfileContext();
    }

    Cookies.remove("token");
    Cookies.remove("sidebarState");
    localStorage.clear();
    navigate("/login?userType=corporate");
  };

  const handleMenuClick = (path) => {
    navigate(path);
  };

  useEffect(() => {
    Cookies.set("sidebarState", isOpenSidebar ? "open" : "closed", {
      expires: 7,
    });
  }, [isOpenSidebar]);

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar
        position="fixed"
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <Toolbar>
          <Tooltip title={isOpenSidebar ? "close sidebar" : "open sidebar"}>
            <IconButton
              color="inherit"
              aria-label="toggle sidebar"
              onClick={() => setIsOpenSidebar(!isOpenSidebar)}
              sx={{
                mr: 2,
                p: 0,
                borderRadius: 2,
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              {isOpenSidebar ? <MenuOpenIcon /> : <MenuIcon />}
            </IconButton>
          </Tooltip>
          <Box
            component="img"
            src="/bb-logo.png"
            alt="BB Logo"
            sx={{
              height: 40,
              mr: 2,
              display: "block",
            }}
          />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            EQuAlly
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Tooltip title="Logout">
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
                sx={{
                  borderRadius: 20,
                  borderColor: "rgba(255, 255, 255, 0.5)",
                  "&:hover": {
                    borderColor: "white",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                Logout
              </Button>
            </Tooltip>
            <Tooltip title="Your Profile">
              <Avatar
                sx={{
                  bgcolor: "secondary.main",
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: "secondary.dark",
                  },
                }}
                onClick={() => navigate("/corporate/profile")}
              >
                {userInitials}
              </Avatar>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>
      <StyledDrawer
        variant="permanent"
        sx={{
          width: isOpenSidebar ? 240 : 64, // Increased width from 200 to 240
          transition: "width 0.3s ease-in-out",
          overflowX: "hidden",
          "& .MuiDrawer-paper": {
            width: isOpenSidebar ? 240 : 64, // Increased width from 200 to 240
            transition: "width 0.3s ease-in-out",
            overflowX: "hidden",
            boxSizing: "border-box", // Ensure proper box sizing
          },
        }}
      >
        <List sx={{ padding: 2 }}>
          {/* Remove default padding */}
          {visibleMenuItems.map((item) => {
            const isSelected = location.pathname === item.path;

            return (
              <Tooltip
                key={item.text}
                title={!isOpenSidebar ? item.text : ""}
                placement="right"
                arrow
              >
                <ListItem
                  button
                  onClick={() => handleMenuClick(item.path)}
                  selected={isSelected}
                  sx={{
                    minHeight: 48,
                    justifyContent: isOpenSidebar ? "flex-start" : "center", // Changed from "initial"
                    px: isOpenSidebar ? 2 : 1, // Reduced padding when closed
                    py: 1.5,
                    width: "100%", // Ensure full width usage
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: isSelected ? "primary.main" : "inherit",
                      minWidth: isOpenSidebar ? 40 : 24, // Adjust icon spacing
                      mr: isOpenSidebar ? 1.5 : 0, // Reduced margin
                      justifyContent: "center",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>

                  {/* Show text only when sidebar is open */}
                  {isOpenSidebar && (
                    <ListItemText
                      primary={item.text}
                      sx={{
                        margin: 0, // Remove default margins
                        "& .MuiTypography-root": {
                          fontSize: "0.875rem", // Slightly smaller font
                          fontWeight: isSelected ? 600 : 400,
                          color: isSelected ? "primary.main" : "inherit",
                          whiteSpace: "nowrap",
                          overflow: "visible", // Changed from hidden to visible
                          textOverflow: "clip", // Changed from ellipsis
                          lineHeight: 1.2,
                        },
                      }}
                    />
                  )}
                </ListItem>
              </Tooltip>
            );
          })}
        </List>
      </StyledDrawer>

      <Main>
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            display: "block",
            width: "100%",
            py: 0,
            px: "5px",
            margin: 0,
            alignItems: "flex-start",
            pt: "64px",
          }}
        >
          {children}
        </Container>
      </Main>
      <ToastContainer />
    </Box>
  );
};

export default CorporateLayout;
